/**
 * Common ProductRailHorizontalCompact component used by other modules as a reusable component which returns the ui for a compact product carousel.
 *
 * @module views/components/ProductRailHorizontalCompact
 * @memberof -Common
 */

import './ProductRailHorizontalCompact.scss';

import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Loader from '@ulta/core/components/Loader/Loader';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils, { manageRailLoading } from '@ulta/utils/carouselUtils/carouselUtils';

/**
 * Represents a ProductRailHorizontalCompact component
 *
 * @method
 * @param { ProductRailHorizontalCompactProps} props - React properties passed from composition
 * @returns ProductRailHorizontalCompact
 */
export const ProductRailHorizontalCompact = function( props ){
  const productRailHorizontalCompactRef = useRef( null );

  const {
    componentKey,
    dataCapture,
    invokeMutation,
    items,
    setShowSpacer,

    /* Rail Title */
    title = defaultProps.title,
    subtitle,
    viewAllAction,

    /* Loader */
    enableLoader = defaultProps.enableLoader,
    loading,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    countLabel = defaultProps.countLabel,
    listPriceAccessibilityLabel,
    listPriceRangeAccessibilityLabel,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction,
    reviewAccessibilityLabel,
    salePriceAccessibilityLabel,
    salePriceRangeAccessibilityLabel
  } = props;

  /* Setting variables */
  const loaderArraySize = 6;
  const productCards = items || [];

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const [loadingHasBeenSeenTrue, setLoadingHasBeenSeenTrue] = useState( false );
  const [showLoader, setShowLoader] = useState( !!enableLoader );
  const [showHeader, setShowHeader] = useState( !enableLoader );

  useEffect( () => {
    manageRailLoading( { items: productCards, loading, loadingHasBeenSeenTrue }, { setShowLoader, setShowHeader } );
  }, [productCards, loading] );

  useEffect( () => {
    if( productCards?.length === 0 ){
      setShowSpacer( false );
    }
    else {
      setShowSpacer( true );
    }
  }, [productCards] );

  useEffect( () => {
    if( loading && !loadingHasBeenSeenTrue ){
      setLoadingHasBeenSeenTrue( true );
    }
  }, [loading, loadingHasBeenSeenTrue] );

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    countLabel: hasItems( productCards ) ? countLabel : '',
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( !showLoader && !hasItems( productCards ) ){
    setShowSpacer( false );
    return null;
  }

  return (
    <div
      className='ProductRailHorizontalCompact'
      ref={ productRailHorizontalCompactRef }
    >
      <GridContainer>
        <Rail
          railRef={ productRailHorizontalCompactRef }
          id={ railUuid }
          items={ productCards }
          previousClickAction={ previousClickAction }
          nextClickAction={ nextClickAction }
          headline={ title }
          headlineTag='h2'
          showCountLabel={ true }
          subheadline={ subtitle }
          viewAllAction={ viewAllAction }
          dataCapture={ dataCapture }
          _railADAmeta={ _railADAmeta }
          _railMeta={ _railMeta }
          slidesPerGroupXL={ 6 }
          sliderPerViewXL={ 6 }
          slidesPerGroupLG={ 6 }
          sliderPerViewLG={ 6 }
          slidesPerGroupMD={ 4 }
          sliderPerViewMD={ 4 }
          slidesPerGroupSM={ 2 }
          sliderPerViewSM={ 2.5 }
        >
          { productCards?.length > 0 &&
            productCards.map( ( product, index ) => {
              return (
                <Slide key={ index }>
                  <div key={ product.skuId + '_' + index }>
                    <AsyncComponent
                      moduleName='ProductCardCompact'
                      { ...spreadProps( { ...product } ) }
                      componentKey={ componentKey }
                      carouselIndex={ index }
                      invokeMutation={ invokeMutation }
                      salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
                      listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                      salePriceRangeAccessibilityLabel={ salePriceRangeAccessibilityLabel }
                      listPriceRangeAccessibilityLabel={ listPriceRangeAccessibilityLabel }
                      { ...( reviewAccessibilityLabel && {
                        reviewAccessibilityLabel: reviewAccessibilityLabel
                      } ) }
                    />
                  </div>
                </Slide>
              );
            } )
          }
          { showLoader &&
            [...Array( loaderArraySize )].map( ( e, index ) => (
              <Slide key={ index }
                aria-hidden={ true }
                className='ProductRailHorizontalCompact__Loader--show'
              >
                <Loader key={ index }
                  displayType='skeleton'
                  multipleLineLoader={ true }
                />
              </Slide>
            ) )
          }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ProductRailHorizontalProps
 * @type {object}
 * @property {string} carouselAccessibility - Value for aria label for product rail
 * @property {string} carouselCardAccessibility - Value for the product card aria label
 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {number} componentKey - Sets the component key
 * @property {string} countLabel - Sets the count label for productRail list
 * @property {boolean} enableLoader - Sets enableLoader to true or false
 * @property {Array.<{promoText: string, reviewCount: Number, rating: Number, brandName: string, productName: string, variantLabel: string, badge: string, image: string, availability: array, addToBagAccessibility: string, additionalOffersText: string, altImage: string, bookmarkAccessibility: string, bookmarked: boolean, kitPrice: string, listPrice: string, salePrice: string, skuId: string}>} items - List of products to loop over and display in carousel
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label
 * @property {string} listPriceRangeAccessibilityLabel - Sets the list price range accessibility label
 * @property {boolean} loading - Sets the loading state to true or false
 * @property {string} nextAccessibility - Sets the aria label for next button
 * @property {object} nextClickAction - This is the action object for the Previous button
 * @property {string} previousAccessibility - Sets the aria label for previous button
 * @property {object} previousClickAction - This is the action object for the Next button
 * @property {string} reviewAccessibilityLabel - Value for the rating aria label
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label
 * @property {string} salePriceRangeAccessibilityLabel - Sets the sale price range accessibility label
 * @property {function} setShowSpacer - Sets the spacer below the component
 * @property {string} subtitle - Sets the subtitle for productRail list
 * @property {string} title - Sets the title for productRail list
 * @property {object} viewAllAction - This is the action object with action label and url
 */
export const propTypes =  {
  /**
   * The prop that holds  aria label for carousel
   */
  carouselAccessibility: PropTypes.string,
  /**
   * The prop that holds  aria label for product card
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * The prop that holds  aria label for carousel
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * Sets the component key
  */
  componentKey: PropTypes.number,
  /**
  * The prop that holds the count label for the product list.
  */
  countLabel: PropTypes.string,
  /**
  * Sets enableLoader to true or false
  */
  enableLoader: PropTypes.bool,
  /**
  * The prop that holds the list of products to render.
  */
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      promoText: PropTypes.string,
      reviewCount: PropTypes.number,
      rating: PropTypes.number,
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantLabel: PropTypes.string,
      badge: PropTypes.string,
      image: PropTypes.object,
      availability: PropTypes.arrayOf( PropTypes.string ),
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      kitPrice: PropTypes.string,
      bookmarkAccessibility: PropTypes.string,
      addToBagAccessibility: PropTypes.string,
      additionalOffersText: PropTypes.string,
      altImage: PropTypes.string,
      bookmarked: PropTypes.bool,
      skuId: PropTypes.string
    } )
  ),
  /**
  * Sets the list price accessibility label
  */
  listPriceAccessibilityLabel: PropTypes.string,
  /**
  * Sets the list price range accessibility label
  */
  listPriceRangeAccessibilityLabel: PropTypes.string,
  /**
  * Sets the loading state to true or false
  */
  loading: PropTypes.bool,
  /**
  * The prop that holds the aria label for next button.
  */
  nextAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
  * The prop that holds the aria label for previous button.
  */
  previousAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the previous button.
   */
  previousClickAction: PropTypes.object,
  /**
  * The prop that decides to show aria-label.
  */
  reviewAccessibilityLabel: PropTypes.string,
  /**
  * Sets the sale price accessibility label
  */
  salePriceAccessibilityLabel: PropTypes.string,
  /**
  * Sets the sale price range accessibility label
  */
  salePriceRangeAccessibilityLabel: PropTypes.string,
  /**
  * Sets the spacer below the component
  */
  setShowSpacer: PropTypes.func,
  /**
  * The prop that holds subtitle for the carousel.
  */
  subtitle: PropTypes.string,
  /**
  * The prop that holds title for the carousel.
  */
  title: PropTypes.string,
  /**
  * This is the action with label and url.
  */
  viewAllAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string

  } )
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} countLabel='' - The default value of the count label is empty string
 * @property {boolean} enableLoader=true - The default value for enableLoader is set to true
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string
 * @property {string} title='' - The default value of the title is empty string
 */
export const defaultProps = {
  countLabel: '',
  enableLoader: true,
  nextAccessibility: '',
  previousAccessibility: '',
  title: ''
};

ProductRailHorizontalCompact.propTypes = propTypes;
ProductRailHorizontalCompact.defaultProps = defaultProps;

export default ProductRailHorizontalCompact;